import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Building2, FileText, Briefcase, Shield, Scale, Users, Tractor, FileSearch, BookOpen, Phone, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useLanguage, useLp } from '../context/LanguageContext';

const serviceIcons = {
  immobiliare: Building2, successioni: FileText, aziende: Briefcase, patrimoniali: Shield, procure: Scale,
  consulenza: Users, agricola: Tractor, ipocatastali: FileSearch, vidimazione: BookOpen
};

const serviceKeys = Object.keys(serviceIcons);

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const { t, services: serviceData } = useLanguage();
  const lp = useLp();
  const service = serviceData[slug];

  useEffect(() => {
    if (service) document.title = `${service.title} | Studio Notarile Accordini & Carillo`;
  }, [service]);

  if (!service) {
    return <Navigate to={lp('/')} replace />;
  }

  const Icon = serviceIcons[slug];
  const currentIndex = serviceKeys.indexOf(slug);
  const prevService = currentIndex > 0 ? serviceKeys[currentIndex - 1] : null;
  const nextService = currentIndex < serviceKeys.length - 1 ? serviceKeys[currentIndex + 1] : null;

  return (
    <div data-testid={`service-detail-${slug}`} className="min-h-screen">
      <SEO page="services" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#121826]" />
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C9A227]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Link to={lp('/servizi')} className="inline-flex items-center gap-2 text-white/40 hover:text-[#C9A227] transition-colors text-sm mb-6">
              <ArrowLeft className="w-4 h-4" /> {t('ui.serviceDetail.all')}
            </Link>
            <div className="flex items-center gap-5 mb-6">
              <div className="w-14 h-14 border border-[#C9A227]/40 flex items-center justify-center">
                <Icon className="w-7 h-7 text-[#C9A227] stroke-[1.5]" />
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold">
                  {service.subtitle}
                </p>
              </div>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 max-w-4xl">
              {service.title}
            </h1>
            <p className="text-white/50 text-base md:text-lg max-w-3xl leading-relaxed">
              {service.intro}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16 md:py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {service.sections.map((section, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                  <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-28">
                      <span className="text-xs font-mono text-[#C9A227] font-bold tracking-wider">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#283880] tracking-tight mt-2 mb-4">
                        {section.title}
                      </h2>
                      <p className="text-[#5A6578] leading-relaxed">
                        {section.content}
                      </p>
                    </div>
                  </div>
                  <div className="lg:col-span-7">
                    <div className="bg-white border border-[rgba(30,36,51,0.08)] shadow-[0_2px_12px_rgba(40,56,128,0.04)] p-8">
                      <ul className="space-y-4">
                        {section.items.map((item, j) => (
                          <li key={j} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-[#C9A227] mt-0.5 flex-shrink-0 stroke-[1.5]" />
                            <span className="text-[#1E2433] text-[15px] leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                {i < service.sections.length - 1 && (
                  <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-[rgba(30,36,51,0.1)] to-transparent mt-16" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {service.faq && service.faq.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-3">
                {t('ui.serviceDetail.faqEyebrow')}
              </p>
              <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#283880] tracking-tight">
                {t('ui.serviceDetail.faqTitle')}
              </h2>
            </motion.div>

            <div className="space-y-6">
              {service.faq.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-[#FAF8F5] border border-[rgba(30,36,51,0.06)] p-6"
                >
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#283880] mb-3">
                    {faq.q}
                  </h3>
                  <p className="text-[#5A6578] leading-relaxed text-[15px]">
                    {faq.a}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Navigation + CTA */}
      <section className="py-12 bg-[#FAF8F5] border-t border-[rgba(30,36,51,0.08)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full lg:w-auto">
              {prevService && (
                <Link to={lp(`/servizi/${prevService}`)} className="min-w-0">
                  <Button variant="outline" className="w-full whitespace-normal text-left leading-snug border-[#283880]/20 text-[#283880] hover:bg-[#283880] hover:text-white rounded-none uppercase tracking-[0.15em] text-[10px] font-bold min-h-11 h-auto py-3 px-6">
                    <ArrowLeft className="w-4 h-4 mr-2 shrink-0" />
                    {serviceData[prevService].title}
                  </Button>
                </Link>
              )}
              {nextService && (
                <Link to={lp(`/servizi/${nextService}`)} className="min-w-0">
                  <Button variant="outline" className="w-full whitespace-normal text-right leading-snug border-[#283880]/20 text-[#283880] hover:bg-[#283880] hover:text-white rounded-none uppercase tracking-[0.15em] text-[10px] font-bold min-h-11 h-auto py-3 px-6">
                    {serviceData[nextService].title}
                    <ArrowRight className="w-4 h-4 ml-2 shrink-0" />
                  </Button>
                </Link>
              )}
            </div>
            <Link to={lp('/contatti')} className="w-full lg:w-auto">
              <Button className="w-full bg-[#C9A227] text-[#121826] hover:bg-[#B38E1B] rounded-none uppercase tracking-[0.15em] text-[10px] font-bold h-11 px-8 shadow-[0_2px_12px_rgba(201,162,39,0.25)]">
                <Phone className="w-4 h-4 mr-2" />
                {t('ui.serviceDetail.cta')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
